function debounce(fn, delay) {
  let timeoutId;

  return function debounced(...args) {
    const context = this;

    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      fn.apply(context, args);
    }, delay);
  };
}

(function () {
  /**
   * Helper: return true if this cart line is a customization item.
   * Customization items are any line whose title contains (case-insensitive):
   *   - "Monogram"
   *   - "Alterations"
   *   - "Cufflink Engraving"
   */
  function isCustomizationItem(item) {
    var rawTitle = (item.product_title || item.title || '').trim().toLowerCase();

    return (
      rawTitle.includes('monogram') ||
      rawTitle.includes('alterations') ||
      rawTitle.includes('cufflink engraving')
    );
  }

  /**
   * Enforce _group_id + customization rules.
   *
   * Customization items are any line whose title contains (case-insensitive):
   *   - "Monogram"
   *   - "Alterations"
   *   - "Cufflink Engraving"
   *
   * Rules:
   * - Customization item with NO _group_id -> remove it.
   * - For each _group_id:
   *    - If customization exists but NO base item -> remove customization item(s).
   *    - If base item(s) exist but NO customization:
   *        - If base title contains "shirt" -> add variant 42022919209150 with same _group_id and qty = baseQty.
   *        - If base title contains "pant" -> add variant 44636189917374 with same _group_id and qty = baseQty.
   *        - If base title contains "cufflink" -> add variant 44636564553918 with same _group_id and qty = baseQty.
   *        - Otherwise -> remove base item(s).
   *    - If both base + customization exist:
   *        - Sum base quantities for the group (baseQty).
   *        - Set the FIRST customization line's quantity = baseQty.
   *        - Set any additional customization lines' quantity = 0.
   *
   * @returns {Promise<{ changed: boolean }>}
   */
  window.ensureEvenGroupCartItems = function () {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) {
          return { changed: false };
        }

        var groups = {};  // { [groupId]: { baseLines: [], customizationLines: [] } }
        var changes = []; // { item, quantity }
        var adds = [];    // { variantId, quantity, groupId }

        // Build groups and collect stray customizations-without-group
        cart.items.forEach(function (item) {
          // Customization detection
          var customization = isCustomizationItem(item);

          // Get _group_id from properties (supports both ._group_id and ['_group_id'])
          var groupId =
            item.properties &&
            (item.properties._group_id || item.properties['_group_id']);

          // 1) Customization with NO _group_id -> remove it
          if (customization && !groupId) {
            changes.push({
              item: item,
              quantity: 0
            });
            return; // don't include in groups
          }

          // 2) Only group items that actually have a _group_id
          if (!groupId) return;

          if (!groups[groupId]) {
            groups[groupId] = {
              baseLines: [],
              customizationLines: []
            };
          }

          if (customization) {
            groups[groupId].customizationLines.push(item);
          } else {
            groups[groupId].baseLines.push(item);
          }
        });

        // Apply rules per group
        Object.keys(groups).forEach(function (groupId) {
          var group = groups[groupId];
          var baseLines = group.baseLines;
          var customizationLines = group.customizationLines;

          // Case A: customization but no base -> remove customization
          if (customizationLines.length && !baseLines.length) {
            customizationLines.forEach(function (item) {
              changes.push({
                item: item,
                quantity: 0
              });
            });
            return;
          }

          // Base quantity = sum of base line quantities (if any)
          var baseQty = baseLines.reduce(function (sum, item) {
            return sum + (item.quantity || 0);
          }, 0);

          // Case B: base but no customization
          if (baseLines.length && !customizationLines.length) {
            var sampleBase = baseLines[0];
            var baseTitle = (sampleBase.product_title || sampleBase.title || '').toLowerCase();
            var variantToAdd = null;

            if (baseTitle.includes('shirt')) {
              variantToAdd = '43337257451690';
            } else if (baseTitle.includes('pant')) {
              variantToAdd = '43337255846058';
            } else if (baseTitle.includes('cufflink')) {
              variantToAdd = '43337257517226';
            }

            if (variantToAdd) {
              // Add appropriate customization product with same group_id and matching quantity
              adds.push({
                variantId: variantToAdd,
                quantity: baseQty,
                groupId: groupId
              });
              return;
            } else {
              // If it's some other base product type, remove the base items (fallback behavior)
              baseLines.forEach(function (item) {
                changes.push({
                  item: item,
                  quantity: 0
                });
              });
              return;
            }
          }

          // Case C: both base + customization present -> sync quantities
          if (!baseLines.length || !customizationLines.length) {
            // Safety guard; above cases should have returned already
            return;
          }

          // Base quantity already computed above: baseQty

          // Normalize customization:
          // - First customization line: qty = baseQty
          // - Any additional customization lines: qty = 0
          var firstCustom = customizationLines[0];

          if ((firstCustom.quantity || 0) !== baseQty) {
            changes.push({
              item: firstCustom,
              quantity: baseQty
            });
          }

          if (customizationLines.length > 1) {
            customizationLines.slice(1).forEach(function (item) {
              if (item.quantity !== 0) {
                changes.push({
                  item: item,
                  quantity: 0
                });
              }
            });
          }
        });

        if (!changes.length && !adds.length) {
          return { changed: false };
        }

        // Apply all /cart/change.js operations sequentially
        function applyChanges() {
          if (!changes.length) return Promise.resolve();

          var index = 0;

          function nextChange() {
            if (index >= changes.length) {
              return Promise.resolve();
            }

            var change = changes[index++];

            return fetch('/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                id: change.item.key,       // line item key
                quantity: change.quantity  // new quantity (0 = remove)
              })
            })
              .then(function () {
                return nextChange();
              })
              .catch(function () {
                // On error, keep going so one bad request doesn't block the rest
                return nextChange();
              });
          }

          return nextChange();
        }

        // Apply all /cart/add.js operations sequentially
        function applyAdds() {
          if (!adds.length) return Promise.resolve();

          var index = 0;

          function nextAdd() {
            if (index >= adds.length) {
              return Promise.resolve();
            }

            var add = adds[index++];

            return fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                id: add.variantId,
                quantity: add.quantity,
                properties: {
                  _group_id: add.groupId
                }
              })
            })
              .then(function () {
                return nextAdd();
              })
              .catch(function () {
                // On error, keep going so one failure doesn't block the rest
                return nextAdd();
              });
          }

          return nextAdd();
        }

        return applyChanges()
          .then(applyAdds)
          .then(function () {
            return { changed: true };
          });
      })
      .catch(function (err) {
        console.error('Error syncing _group_id customization items:', err);
        return { changed: false, error: err };
      });
  };

  /**
   * Refresh the cart drawer section using Shopify's Section Rendering API.
   * Assumes your cart drawer is a section with id="shopify-section-cart-drawer"
   * (standard for a section file named cart-drawer.liquid).
   */
  window.refreshCartDrawer = async function refreshCartDrawer() {
    const sectionName = 'cart-drawer';
    const sectionWrapperId = `shopify-section-${sectionName}`;

    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionName);

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    });

    if (!res.ok) {
      throw new Error(`Failed to refresh ${sectionName}: ${res.status}`);
    }

    const data = await res.json();
    const html = data && data[sectionName];
    if (!html) {
      throw new Error(`Section HTML missing for "${sectionName}"`);
    }

    const current = document.querySelector(sectionName);
    if (!current) {
      throw new Error(`Could not find #${sectionWrapperId} in the DOM`);
    }

    // Parse returned section HTML and extract the fresh wrapper
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh =
      doc.getElementById(sectionWrapperId) || doc.body.firstElementChild;
    if (!fresh) {
      throw new Error(`Could not parse fresh section wrapper for "${sectionName}"`);
    }

    current.innerHTML = fresh.querySelector("cart-drawer").innerHTML

    // Optional: if your drawer JS needs a hook after swap
    document.dispatchEvent(new CustomEvent('cart-drawer:refreshed'));
  };

  /**
   * Core pipeline for post-cart-update behavior:
   * 1) Enforce _group_id + customization rules
   * 2) Refresh cart drawer **only if the cart changed**
   *
   * This returns a Promise. Use this if you need to await it.
   */
  function runCartCleanupAndRefreshCore() {
    console.log("-- validate monograms --")
    return window.ensureEvenGroupCartItems()
      .then(function (result) {
        var changed = result && result.changed;

        // Only refresh the cart drawer if something actually changed
        if (changed && typeof window.refreshCartDrawer === 'function') {
          return window.refreshCartDrawer().then(function () {
            return result;
          });
        }

        return result;
      })
      .catch(function (err) {
        console.error('Error running cart cleanup / refresh:', err);
      });
  }

  // Non-debounced version (returns Promise)
  window.runCartCleanupAndRefresh = function () {
    return runCartCleanupAndRefreshCore();
  };

  // Debounced version (fire-and-forget; return value is not meaningful)
  window.runCartCleanupAndRefreshDebounced = debounce(runCartCleanupAndRefreshCore, 500);

  // Auto-run once on page load (debounced to survive multiple triggers)
  document.addEventListener('DOMContentLoaded', function () {
    window.runCartCleanupAndRefreshDebounced();
  });
})();